import { initializeApp } from "firebase/app";
import { getDatabase } from "firebase/database";
import { getStorage } from "firebase/storage";
import { getAuth, signInAnonymously } from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

let storage = null;
let db = null;
// Resolves once anonymous sign-in settles. One-shot Storage reads
// (getDownloadURL on /screenshots/*, which the rules gate on auth != null)
// must await this — unlike RTDB listeners, getDownloadURL does NOT auto-retry
// when the auth token lands, so calling it before sign-in completes is a race
// that surfaces as "Screenshot not available". Defaults to resolved when
// Firebase isn't configured.
let authReady = Promise.resolve();

if (firebaseConfig.apiKey) {
  const app = initializeApp(firebaseConfig);
  storage = getStorage(app);
  db = getDatabase(app);

  // RTDB rules require `auth != null` (see popuppush-iac firebase rules).
  // Without an anonymous sign-in here every onValue() silently returns
  // `snapshot.exists() === false`, so the Screens page kept showing
  // devices as offline (RTDB listener never saw lastLogin) even when
  // MySQL knew they were online — the dashboard counters use MySQL via
  // the backend, hence the inconsistency. The Firebase SDK auto-applies
  // the new auth token to in-flight listeners as soon as sign-in
  // resolves, so existing components don't need to wait.
  // Anonymous sign-in can fail on some 4G/mobile networks (the auth endpoint
  // or RTDB WebSocket is blocked). We log + continue rather than break the
  // app: device cards fall back to the backend `online` flag from the device
  // payload (see resolveDeviceOnline in src/utils/helpers.js), so the Screens
  // page still shows correct online/offline status without RTDB. We also retry
  // once after a short delay so a transient failure self-heals without a reload.
  const auth = getAuth(app);
  authReady = new Promise((resolve) => {
    const trySignIn = (retriesLeft) => {
      signInAnonymously(auth)
        .then(() => resolve())
        .catch((err) => {
          console.warn("[firebase] anonymous sign-in failed", err?.code || err);
          if (retriesLeft > 0) {
            setTimeout(() => {
              if (auth.currentUser) resolve();
              else trySignIn(retriesLeft - 1);
            }, 3000);
          } else {
            resolve(); // stop blocking reads; they'll surface their own error
          }
        });
    };
    if (auth.currentUser) resolve();
    else trySignIn(1);
  });
}

export { storage, db, authReady };
